import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

import { EditorView } from "prosemirror-view";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKitExtension from "@tiptap/starter-kit";
import UnderlineExtension from "@tiptap/extension-underline";
import { Color as ColorExtension } from "@tiptap/extension-color";
import TextStyleExtension from "@tiptap/extension-text-style";
import PlaceholderExtension from "@tiptap/extension-placeholder";
import CharacterCountExtension from "@tiptap/extension-character-count";
import LinkExtension from "@tiptap/extension-link";
import { makeStyles } from "@material-ui/core";
import Toolbar from "./Toolbar";

const defaultTextColor = "#000";
const useStyles = makeStyles({
	root: {
		display: "flex",
		flexDirection: "column",
		height: (props) => (props.maxHeight ? props.maxHeight : "100%"),
		minHeight: (props) => props.height || 324,
		maxHeight: (props) => props.height || 324,
		transition: "height 0.2s ease, max-height 0.2s ease, min-height 0.2s ease",
		"&$collapsed": {
			minHeight: 0,
			maxHeight: 20,
			height: 20,
			overflow: "hidden",
		},
	},
	collapsed: {
		// required for above snippet to work
	},
	inner: {
		height: "100%",
		overflow: "visible",
		display: "flex",
		flexDirection: "column",
	},
	editorContainer: {
		flexGrow: 1,
		height: (props) => `calc(100% - ${props.characterLimit ? 52 : 26}px)`,
		"&::after": {
			content: (props) => (props.error ? `"Response message is required"` : `""`),
			position: "absolute",
			color: "#f44336",
			margin: 0,
			fontSize: "0.75rem",
			marginTop: 3,
			textAlign: "left",
			fontFamily: `"Roboto", "Helvetica", "Arial", "sans-serif"`,
			fontWeight: 400,
			lineHeight: 1.66,
			letterSpacing: "0.03333em",
		},
	},
	editor: {
		height: "100%",
		overflow: "auto",
		border: "2px solid rgba(0, 0, 0, 0.2)",
		borderRadius: 4,
		padding: 4,
		wordBreak: "break-word",
		"&.ProseMirror-focused": {
			outline: "unset",
			boxShadow: "none",
			borderColor: (props) => (props.error ? "#f44336" : "rgba(0, 0, 0, 0.8)"),
		},
		"&:not(.ProseMirror-focused)": {
			borderColor: (props) => (props.error ? "#f44336" : "rgba(0, 0, 0, 0.2)"),
			"&:hover:not([contenteditable=false])": {
				borderColor: (props) => (props.error ? "#c6372c" : "rgba(0, 0, 0, 0.35)"),
			},
		},
		"&[contenteditable=false]": {
			color: "#757679",
		},
		"&.ProseMirror p.is-editor-empty:first-child::before": {
			// placeholder
			content: "attr(data-placeholder)",
			color: "#adb5bd",
			float: "left",
			height: 0,
			pointerEvents: "none",
		},
	},
	characterCount: {
		height: 20,
		marginTop: 6,
		textAlign: "right",
	},
});

// https://tiptap.dev/api/extensions/starter-kit
const requiredExtensions = {
	document: true, // required, keep enabled
	hardBreak: true, // new lines, recommended enabled
	paragraph: true, // essential, keep enabled
	text: true, // required, keep enabled
};

const defaultExtensions = {
	blockquote: false,
	bulletList: true,
	codeBlock: false,
	heading: true,
	horizontalRule: false,
	listItem: true, // must be enabled if bullet or ordered lists are enabled
	orderedList: true,
	bold: true,
	code: false,
	italic: true,
	underline: true,
	strike: true,
	dropcursor: false,
	grapcursor: false,
	history: true, // recommended enabled
	color: true,
	link: true,
};

const EMPTY_PARAGRAPH_REGEX = /(<p\s?style=["|'][a-zA-Z0-9:;.\s()\-,]*["|']?>)(<\/p>)/g;
const BR_PARAGRAPH_REGEX = /(<p\s?style=["|'][a-zA-Z0-9:;.\s()\-,]*["|']?>)<br\s?\/?>(<\/p>)/g;

/**
 * This component renders a rich text field that supports styling nodes (e.g. bold, italics, etc.)
 * @param {string} props.value The string value for the text field
 * @param {function} props.onChange Function to be called when the value changes
 * @param {number} props.height Height of the text field, needed for styling as the field expands/collapses
 * @param {number} props.maxHeight Maximum allowed height of the text field, needed for styling as the field expands/collapses
 * @param {Component} props.toolbarActions An array of button components to be rendered in the toolbar as actions
 * @param {boolean} props.disabled Whether the text field should be disabled
 * @param {boolean} props.readonly Whether the text field should be read-only
 * @param {string} props.placeholder Placeholder string to be shown in text field
 * @param {number} props.characterLimit Limit on how many characters are allowed in the text field, will cause a character counter to show
 * @param {object} props.extensions An object of which extensions are to be enabled/disabled, refer to `defaultExtensions` in this component
 * @param {boolean} props.overrideExtensions Whether the prop `extensions` will modify the default status of extensions, or they should be the only source of truth for which extensions are enabled/disabled
 * @param {boolean} props.preserveHtmlLineBreaks Insert line breaks into empty paragraphs since the html output does not include them
 */
const RichTextField = (props) => {
	const {
		value,
		onChange,
		toolbarActions,
		disabled,
		placeholder,
		characterLimit,
		extensions: propExtensions,
		overrideExtensions,
		readOnly,
		preserveHtmlLineBreaks,
	} = props;
	const classes = useStyles(props);
	const [expanded, setExpanded] = useState(true);
	const [headingLevel, setHeadingLevel] = useState(null);
	const [textColor, setTextColor] = useState(defaultTextColor);
	// required extensions will always be enabled
	// if `overrideExtensions` is true, only the extensions passed in prop are taken into account
	// if not, the prop values are merged with the `defaultExtensions` for a complete list
	let extensions = {
		...requiredExtensions,
		...(propExtensions
			? {
					...(overrideExtensions
						? Object.fromEntries(Object.entries(defaultExtensions).map(([key]) => [key, false]))
						: { defaultExtensions }),
					...propExtensions,
			  }
			: defaultExtensions),
	};
	if (extensions.bulletList || extensions.orderedList) {
		// required if lists are enabled
		extensions.listItem = true;
	}

	const editor = useEditor({
		extensions: [
			StarterKitExtension.configure({
				...extensions,
				...(extensions.paragraph
					? {
							// get rid of margin on all paragraphs
							paragraph: {
								HTMLAttributes: {
									style: "margin: 0",
								},
							},
					  }
					: {}),
			}),
			...(extensions.underline ? [UnderlineExtension] : []),
			...(extensions.color ? [TextStyleExtension, ColorExtension] : []),
			...(extensions.link
				? [
						// enabled to allow links, but will need to add button to toolbar
						LinkExtension.configure({
							autolink: true,
							openOnClick: true,
						}),
				  ]
				: []),
			PlaceholderExtension.configure({
				placeholder,
				showOnlyCurrent: false,
				showOnlyWhenEditable: false,
			}),
			CharacterCountExtension.configure({
				limit: characterLimit,
			}),
		],
		// this is the reverse of what happens in the update event listener
		// we need to remove any <br> tags from a paragraph which only contains a line break
		// since ProseMirror ends up adding one for us anyway... (why????)
		// more info here: https://github.com/ueberdosis/tiptap/issues/1500
		content: value.replaceAll(BR_PARAGRAPH_REGEX, "$1$2"),
		onSelectionUpdate({ editor }) {
			setHeadingLevel(editor.getAttributes("heading")?.level);
			setTextColor(editor.getAttributes("textStyle")?.color || defaultTextColor);
		},
		editorProps: {
			attributes: {
				class: classes.editor,
			},
		},
		editable: !readOnly,
	});

	/*
	 * ok, so, what's happening here?
	 *
	 * the problem with just setting `onUpdate: ({ editor }) => {...}` in `useEditor` means
	 * when the `onChange` callback function is fired, its latest version will not be used.
	 *
	 * now, if we set `value` and `onChange` as dependencies for `useEditor` to ensure
	 * the latest version of both are used and cause a component re-render, the editor
	 * loses focus every time one of them changes and a re-render happens... which is no good...
	 *
	 * so instead, we let the editor use its internal state for the value
	 * (since it seems to do that without having to re-render)
	 * and we re-apply a listener for the update event here to call our callbacks,
	 * which allows `useEffect` to use the latest version of the `onChange` callback function
	 *
	 * potentially more helpful info here: https://github.com/ueberdosis/tiptap/issues/2403
	 */
	useEffect(() => {
		if (!editor) {
			return;
		}

		editor.off("update");
		editor.on("update", ({ editor: updatedEditor }) => {
			let html = updatedEditor.getHTML();

			if (preserveHtmlLineBreaks) {
				// insert line breaks into empty paragraphs since the html output does not include them
				// more info here: https://github.com/ueberdosis/tiptap/issues/412
				html = html.replaceAll(EMPTY_PARAGRAPH_REGEX, "$1<br>$2");
			}

			if (onChange && typeof onChange === "function") {
				const valueLength = editor.storage.characterCount.characters();
				onChange(valueLength > 0 ? html : "");
			}

			setHeadingLevel(updatedEditor.getAttributes("heading")?.level);
			setTextColor(updatedEditor.getAttributes("textStyle")?.color || defaultTextColor);
		});
	}, [editor, onChange, preserveHtmlLineBreaks]);

	useEffect(() => {
		editor?.setEditable(!disabled && !readOnly);
	}, [disabled, readOnly, editor]);

	useEffect(() => {
		if (!value || value === "") {
			editor?.commands.setContent("");
		}
	}, [value, editor]);

	const handleChangeColor = (color) => {
		editor.commands.setColor(color);
		setTextColor(color);
	};

	return (
		<div className={clsx(classes.root, !expanded && classes.collapsed)}>
			<div className={classes.inner}>
				{!readOnly && (
					<Toolbar
						editor={editor}
						expanded={expanded}
						setExpanded={setExpanded}
						actions={toolbarActions}
						disableBtns={disabled}
						headingLevel={headingLevel}
						color={textColor}
						setColor={handleChangeColor}
						extensions={extensions}
					/>
				)}

				<EditorContent editor={editor} className={classes.editorContainer} />

				{!readOnly && characterLimit && editor && (
					<div className={classes.characterCount} data-test-id="rich-text-char-counter">
						{`${editor.storage.characterCount.characters()}/${characterLimit}`}
					</div>
				)}
			</div>
		</div>
	);
};

EditorView.prototype.updateState = function updateState(state) {
	if (!this.docView) return; // This prevents the matchesNode error on hot reloads
	this.updateStateInner(state, this.state.plugins !== state.plugins);
};

RichTextField.propTypes = {
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	value: PropTypes.string,
	onChange: PropTypes.func,
	toolbarActions: PropTypes.arrayOf(PropTypes.element),
	disabled: PropTypes.bool,
	placeholder: PropTypes.string,
	characterLimit: PropTypes.number,
	extensions: PropTypes.object,
	overrideExtensions: PropTypes.bool,
	readOnly: PropTypes.bool,
};

RichTextField.defaultProps = {
	value: "",
	disabled: false,
	overrideExtensions: false,
	readOnly: false,
};

export default RichTextField;
